/**
 * Real-time Price Fetching for Optional Add-ons
 * Ensures all addon prices are fetched in real-time and properly formatted
 */

(function() {
  'use strict';

  // Cache for product data to avoid redundant API calls
  const productCache = new Map();

  // Price formatting function (matches Shopify's format)
  function formatMoney(cents, format = "{{amount}}") {
    if (typeof cents === 'string') cents = parseInt(cents.replace(/\D/g, ''));

    const value = (cents / 100).toFixed(2);

    // Basic UK format (adjust if needed for different currency)
    let formatted = format.replace('{{amount}}', value);
    formatted = formatted.replace('{{amount_no_decimals}}', Math.floor(cents / 100));
    formatted = formatted.replace('{{amount_with_comma_separator}}', value.replace('.', ','));

    // Add currency symbol
    formatted = formatted.replace(/\{\{\s*\w+\s*\}\}/g, '');
    return `£${formatted}`;
  }

  // Fetch product data from Shopify API
  async function fetchProductData(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');

      const product = await response.json();
      productCache.set(handle, product);
      return product;
    } catch (error) {
      console.error(`Error fetching product ${handle}:`, error);
      return null;
    }
  }

  // Update addon prices in the DOM
  async function updateAddonPrices() {
    // Find all addon price elements
    const priceElements = document.querySelectorAll([
      '[data-product-price]',
      '.addon-upsell-price',
      '.mattress-drawer-price-current .price-current',
      '.cart-addon-current-price'
    ].join(','));

    for (const el of priceElements) {
      let price = el.getAttribute('data-variant-price') ||
                  el.getAttribute('data-product-price') ||
                  el.textContent;

      // If price is already formatted, skip
      if (typeof price === 'string' && price.includes('£')) continue;

      // Get variant ID if available
      const variantId = el.getAttribute('data-variant-id') ||
                        el.closest('[data-variant-id]')?.getAttribute('data-variant-id');

      // Get product handle if available
      const productHandle = el.getAttribute('data-product-handle') ||
                           el.closest('[data-product-handle]')?.getAttribute('data-product-handle');

      // If we have a variant ID or product handle, fetch fresh data
      if (variantId || productHandle) {
        try {
          let productData = null;

          if (productHandle) {
            productData = await fetchProductData(productHandle);
          } else if (variantId) {
            // Try to find product by variant ID
            const cart = await fetch('/cart.js').then(r => r.json());
            const item = cart.items.find(i => i.variant_id == variantId);
            if (item) {
              productData = {
                variants: [item],
                price: item.price,
                compare_at_price: item.compare_at_price
              };
            }
          }

          if (productData) {
            let variant = productData.variants?.find(v => v.id == variantId) ||
                         productData.variants?.[0];

            if (variant) {
              price = variant.price;

              // Update the element with fresh price
              el.textContent = formatMoney(price);

              // Update data attributes
              el.setAttribute('data-variant-price', price);
              el.setAttribute('data-product-price', formatMoney(price));

              // Update compare price if exists
              if (variant.compare_at_price && variant.compare_at_price > price) {
                const compareEl = el.closest('.price-box')?.querySelector('.price-compare') ||
                                 el.parentElement?.querySelector('.cart-addon-compare-price') ||
                                 el.closest('[data-product-compare-price]');
                if (compareEl) {
                  compareEl.textContent = formatMoney(variant.compare_at_price);
                }
              }

              // Dispatch event for other scripts to listen
              el.dispatchEvent(new CustomEvent('priceUpdated', {
                detail: {
                  variantId: variant.id,
                  price: price,
                  formattedPrice: formatMoney(price)
                }
              }));
            }
          }
        } catch (error) {
          console.error('Error updating price:', error);
        }
      } else if (price && !isNaN(price)) {
        // Just format the existing numeric price
        el.textContent = formatMoney(price);
      }
    }
  }

  // Initialize on DOM content loaded
  function init() {
    // Initial update
    updateAddonPrices();

    // Set up mutation observer for dynamic content
    const observer = new MutationObserver(mutations => {
      let shouldUpdate = false;

      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          // Check if any price-related elements were added
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.matches && node.matches('[data-product-price], [data-variant-price], .addon-upsell-price, .mattress-drawer-price-current')) {
                shouldUpdate = true;
              } else if (node.querySelector) {
                const priceEls = node.querySelectorAll('[data-product-price], [data-variant-price], .addon-upsell-price, .mattress-drawer-price-current');
                if (priceEls.length > 0) shouldUpdate = true;
              }
            }
          });
        }
      });

      if (shouldUpdate) {
        // Debounce the update
        setTimeout(updateAddonPrices, 100);
      }
    });

    // Start observing the document
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Update prices when variant changes
    document.addEventListener('variantChange', updateAddonPrices);

    // Update prices when cart is updated
    document.addEventListener('cart:updated', updateAddonPrices);
    document.addEventListener('cart:refresh', updateAddonPrices);
  }

  // Make functions globally available
  window.RealtimeAddonPrices = {
    updatePrices: updateAddonPrices,
    formatMoney: formatMoney,
    fetchProduct: fetchProductData
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run after Shopify sections load
  document.addEventListener('shopify:section:load', init);
})();